import z from 'zod';
import { FileUploadMimeType } from './fileUploadSchema.js';
import {
  ActivityDataEntityType,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  TrackedEntity,
  TrackedEntityType,
} from '../common.js';
import { DateTimeSchema } from '../dates.js';
import { ChatPromptType } from '../llmResponseSchemas.js';
import { ActivityData, LlmInputMetadata } from '../../interfaces.js';

export const MessageRole = z.enum(['user', 'assistant', 'system', 'tool']);
export type MessageRole = z.infer<typeof MessageRole>;

export const MessageStatus = z.enum(['pending', 'processing', 'complete', 'failed', 'terminated']);
export type MessageStatus = z.infer<typeof MessageStatus>;

// Tool call schema for assistant messages that invoke tools
export const ToolCallStatus = z.enum(['completed', 'failed']);
export type ToolCallStatus = z.infer<typeof ToolCallStatus>;

export const IToolCall = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  description: z.string().optional(),
  arguments: z.string().optional(),
  result: z.unknown().optional(),
  status: ToolCallStatus.optional(),
  durationMs: z.number().optional(),
});
export type IToolCall = z.infer<typeof IToolCall>;

const AttachmentType = z.enum(['image']);

export const ImageDetail = z.enum(['low', 'high', 'auto']);
export type ImageDetail = z.infer<typeof ImageDetail>;

export const IChatAttachment = z.object({
  type: AttachmentType,
  mimeType: FileUploadMimeType,
  name: z.string().optional(),
  detail: ImageDetail.optional(),
  signedUrl: z.string().optional(),
  fileUpload: objectIdRef('FileUpload'),
});
export type IChatAttachment = z.infer<typeof IChatAttachment>;

export const ChatAttachmentDTO = z.object({
  ...IChatAttachment.shape,
  fileUpload: ObjectIdString,
});
export type ChatAttachmentDTO = z.infer<typeof ChatAttachmentDTO>;

/*
 * Image ready to be sent to the LLM
 * TODO: Expand this section to support other file types and clean up the overrides
 */
export interface ChatImageLlmInput extends ChatAttachmentDTO {
  type: 'image';
  signedUrl: string;
}

export const AttachmentInput = z.object({
  type: AttachmentType,
  mimeType: FileUploadMimeType,
  name: z.string().optional(),
  detail: ImageDetail.optional(),
  fileUpload: ObjectIdString,
});
export type AttachmentInput = z.infer<typeof AttachmentInput>;

export const TrailItemType = z.enum(['url', 'atsPage', ...TrackedEntityType.options]);
export type TrailItemType = z.infer<typeof TrailItemType>;

const BaseNavTrailItem = z.object({
  type: TrailItemType,
  // TODO: make visitedAt required once trails written before it existed are gone
  visitedAt: DateTimeSchema.optional(),
});

export const EntityTrailItem = BaseNavTrailItem.safeExtend({
  ...TrackedEntity.shape,
  profileSnapshotId: ObjectIdString.optional(),
});
export type EntityTrailItem = z.infer<typeof EntityTrailItem>;

export const UrlTrailItem = BaseNavTrailItem.safeExtend({
  type: z.literal('url'),
  url: z.string(),
});
export type UrlTrailItem = z.infer<typeof UrlTrailItem>;

/*
 * An ATS page with no tracked record, as the user sees it: the tab text and 'List' or the
 * record type. Both are absent when nothing is open at all
 */
export const AtsPage = z.object({
  type: z.literal('atsPage'),
  label: z.string().nullish(),
  kind: z.string().nullish(),
});
export type AtsPage = z.infer<typeof AtsPage>;

// What is open in the ATS, a tracked record or an untracked page
export const AtsView = z.discriminatedUnion('type', [TrackedEntity, AtsPage]);
export type AtsView = z.infer<typeof AtsView>;

export const AtsPageTrailItem = BaseNavTrailItem.safeExtend({ ...AtsPage.shape });
export type AtsPageTrailItem = z.infer<typeof AtsPageTrailItem>;

export const NavigationTrailItem = z.discriminatedUnion('type', [
  EntityTrailItem,
  UrlTrailItem,
  AtsPageTrailItem,
]);
export type NavigationTrailItem = z.infer<typeof NavigationTrailItem>;

export const IChatMessage = z.object({
  _id: ObjectIdSchema,
  role: MessageRole,
  content: z.string(),
  timestamp: DateTimeSchema,
  tokenCount: z.number().optional(),
  metadata: z.looseObject({}).optional(),
  status: MessageStatus.optional(),
  error: z.string().optional(),
  switchedTo: NavigationTrailItem.optional(), // The place a [Context update] marks a switch to
  contextKey: z.string().optional(), // Location + record a [Context update] reports, so a repeat can be skipped
  llmResponse: objectIdRef('LlmResponse').optional(), // Reference to LlmResponse for assistant messages
  attachments: IChatAttachment.array().optional(),
  // Tool call support
  toolCallId: z.string().optional(), // For 'tool' role messages (result of a tool call)
  toolName: z.string().optional(), // For 'tool' role messages
  toolCalls: IToolCall.array().optional(), // For 'assistant' role messages that invoke tools
});
export type IChatMessage = z.infer<typeof IChatMessage>;

export const ChatMessageDTO = z.object({
  ...IChatMessage.shape,
  _id: ObjectIdString,
  llmResponse: ObjectIdString.optional(),
  attachments: ChatAttachmentDTO.array().optional(),
});
export type ChatMessageDTO = z.infer<typeof ChatMessageDTO>;

export const ChatStatus = z.enum(['active', 'complete', 'error', 'archived']);
export type ChatStatus = z.infer<typeof ChatStatus>;

// Tools cache schema for storing built tool definitions
// The tools array stores ChatCompletionTool objects from OpenAI
// We use z.unknown() for tools since they're just passed through to/from OpenAI
export const ToolsCache = z.object({
  tools: z.array(z.unknown()),
  cachedAt: z.number(),
  configKey: z.string().optional(),
});
export type ToolsCache = z.infer<typeof ToolsCache>;

export const ILlmChat = z.object({
  _id: ObjectIdSchema,
  account: objectIdRef('Account'),
  entity: ObjectIdSchema.optional(),
  entityType: ActivityDataEntityType.optional(),
  // user: ObjectIdReference,
  recruiter: objectIdRef('Recruiter'),
  prompt: objectIdRef('LlmPrompt'), // For audit and persisting chat with same prompt throughout session
  opportunity: objectIdRef('Opportunity').optional(), // In the case of chat based on extension opportunity
  promptType: ChatPromptType,
  status: ChatStatus,
  messages: IChatMessage.array(),
  metadata: LlmInputMetadata,
  data: z.object({
    activityData: ActivityData.nullish(), // Cached activityData to not recalculate every turn of the chat
    // TODO: update this and make a real schema
    opportunityData: z.looseObject({}).nullish(),
    candidate: z.looseObject({}).nullish(), // Current state of candidate for audit purposes
    contact: z.looseObject({}).nullish(),
    company: z.looseObject({}).nullish(),
    recruiter: z.looseObject({}).nullish(), // Current state of recruiter for audit purposes
  }),
  isExtension: z.boolean().optional(),
  impersonatedBy: objectIdRef('AuthUser').nullish(), // Stamped when the session was created during an admin impersonation session
  model: z.string().optional(),
  error: z.unknown().optional(),
  toolsCache: ToolsCache.optional(), // Cached tool definitions for this chat session
  navigationTrail: NavigationTrailItem.array().optional(), // ATS record, URL of other site, or untracked ATS page
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type ILlmChat = z.infer<typeof ILlmChat>;

export const ChatOutput = z.object({
  messages: ChatMessageDTO.array(),
  status: ChatStatus,
  sessionId: ObjectIdString,
  opportunityId: ObjectIdString.optional(),
  navigationTrail: NavigationTrailItem.array().optional(),
});
export type ChatOutput = z.infer<typeof ChatOutput>;
